<template>
  <ContainerArea class="ArticleDetails" pa-0 fluid>
    <LayoutFill :no-gutters="solidHeader" :no-padding="solidHeader">
      <ResearchDirectionsBannerTop v-if="isResearchDirectionsItem" />
      <MainBanner
        :category="mainCategory"
        :subject="article.subjectType"
        :solid-header="solidHeader"
        :is-subject-link="true"
        :rss-feed="true"
        :search="true"
      />
    </LayoutFill>
    <LayoutGutters v-if="newArticleDetailsDisplay" :no-padding="true">
      <ArticleNotification :article="article" />
    </LayoutGutters>
    <LayoutGutters>
      <v-row>
        <v-col
          v-if="articleDetailsShowNetworkMap && isResearchDirectionsItem"
          cols="12"
          md="8"
        >
          <ArticleHeaderTitle :article="article" />
          <ArticleHeader
            :article="article"
            :citation-style="citationStyle"
            @scroll-to-comment-section="scrollToComments"
            @start-video="startVideo"
          />
          <v-tabs v-model="currentTab" show-arrows class="my-3">
            <v-tab v-for="tab in tabs" :key="tab.key" :href="`#${tab.key}`">
              <span>{{ tab.title }}</span>
              <div
                v-if="tab.itemCount"
                class="softened rounded-16 min-width-32 height-24 py-1 ml-2"
              >
                {{ tab.itemCount }}
                <span class="d-sr-only">{{ tab.itemCountAria }}</span>
              </div>
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="currentTab">
            <v-tab-item value="item">
              <ArticleContent
                :article="article"
                :citation-style="citationStyle"
                :autoplay="autoplay"
                video-usage
              />
            </v-tab-item>
            <v-tab-item value="connected-items">
              <div class="network-map">
                <network-map :data-id="article.coreProductId"></network-map>
              </div>
            </v-tab-item>
          </v-tabs-items>
          <ArticleComments
            v-if="articleDetailsShowComments"
            ref="scrollElement"
            :article="article"
            :recaptcha-site-key="recaptchaSiteKey"
          />
        </v-col>
        <v-col v-else cols="12" md="8">
          <ArticleHeaderTitle :article="article" />
          <ArticleHeader
            :article="article"
            :citation-style="citationStyle"
            @scroll-to-comment-section="scrollToComments"
            @start-video="startVideo"
          />
          <ArticleContent
            :article="article"
            :citation-style="citationStyle"
            :autoplay="autoplay"
            video-usage
          />
          <ArticleComments
            v-if="articleDetailsShowComments"
            ref="scrollElement"
            :article="article"
            :recaptcha-site-key="recaptchaSiteKey"
          />
        </v-col>
        <v-col v-if="displayIsMobile || displayIsTablet" cols="12">
          <ArticleMetadata
            :article="article"
            :buttons-id="shareaholicButtonsId"
          />
        </v-col>
        <v-col
          v-if="displayIsDesktop"
          cols="4"
          class="d-flex pr-2 pl-1 pt-1 pb-5"
        >
          <ArticleMetadata
            :article="article"
            :buttons-id="shareaholicButtonsId"
          />
        </v-col>
      </v-row>
      <ResearchDirectionsBannerBottom v-if="isResearchDirectionsItem" />
    </LayoutGutters>
  </ContainerArea>
</template>

<script>
import { mapState } from "vuex";
import { format, parseISO } from "date-fns";
import mapFlags from "~/plugins/utilities/mapFlags";
import buildActionDispatcher from "~/actions/utilities/buildActionDispatcher";

import ContainerArea from "~/components/layout/ContainerArea";
import LayoutFill from "~/components/layout/LayoutFill";
import LayoutGutters from "~/components/layout/LayoutGutters.vue";

import {
  displayIsMobile,
  displayIsTablet,
  displayIsDesktop,
} from "~/components/utilities/displayBreakpoints";

import MainBanner from "~/components/banner/MainBanner.vue";
import ArticleContent from "~/components/articles/ArticleContent.vue";
import ArticleHeader from "~/components/articles/ArticleHeader.vue";
import ArticleMetadata from "~/components/articles/ArticleMetadata.vue";
import ArticleComments from "~/components/articles/ArticleComments.vue";
import ArticleHeaderTitle from "~/components/articles/ArticleHeaderTitle.vue";
import ArticleNotification from "~/components/articles/components/ArticleNotification.vue";

import sorted from "~/plugins/utilities/sorted.js";
import toCanonicalUrl from "~/plugins/utilities/toCanonicalUrl.js";
import getPageTitle from "~/plugins/utilities/getPageTitle.js";
import getSiteName from "~/plugins/utilities/getSiteName.js";
import { html2text } from "~/plugins/utilities/stripHtml";

import ResearchDirectionsBannerTop from "~/components/research-directions/ResearchDirectionsBannerTop.vue";
import ResearchDirectionsBannerBottom from "~/components/research-directions/ResearchDirectionsBannerBottom.vue";
import isLoggedIn from "~/plugins/utilities/isLoggedIn";

export default {
  name: "ArticleDetails",
  components: {
    ContainerArea,
    LayoutFill,
    LayoutGutters,
    MainBanner,
    ArticleContent,
    ArticleHeaderTitle,
    ArticleHeader,
    ArticleMetadata,
    ArticleComments,
    ArticleNotification,
    ResearchDirectionsBannerTop,
    ResearchDirectionsBannerBottom,
  },
  asyncData(context) {
    const shareaholicButtonsId =
      context.$config.partner === "apsa"
        ? context.$config.SHAREAHOLIC_APSA_BTNS_ID
        : context.$config.SHAREAHOLIC_BTNS_ID;

    return {
      recaptchaSiteKey: context.$config.RECAPTCHA_SITE_KEY,
      shareaholicButtonsId,
    };
  },
  data() {
    return {
      autoplay: false,
    };
  },
  async fetch(context) {
    const { $config, params, redirect, error } = context;

    const partner = $config.partner;
    const data = await buildActionDispatcher("loadArticleDetails")(context);
    const selectedItem = data ? data.selectedItem : null;
    const itemOrigin = selectedItem?.origin.toLowerCase();

    if (!selectedItem) {
      redirect(`/engage/${partner}/article-details/not-found/${params.id}`);
    }

    if (itemOrigin !== partner) {
      redirect(`/engage/${itemOrigin}/article-details/${params.id}`);
    }

    if (selectedItem?.status === "WITHDRAWN") {
      error({ statusCode: 410, message: selectedItem.withdrawalReason });
    }
  },
  head() {
    const { article, partner, siteName, mainCategoryName } = this;
    const {
      id,
      title,
      doi,
      abstract,
      retraction,
      subjectType = {},
      asset = {},
      keywords = [],
      authors = [],
    } = article || {};
    const { original = {}, largeThumbnail = {} } = asset || {};

    const strippedAbstract = html2text(this.$sanitize, abstract);
    const strippedTitle = html2text(this.$sanitize, title);

    const authorGoogleMetas = [];
    const authorDCMetas = [];

    const warning =
      retraction &&
      retraction.response &&
      retraction.response.retractionStatus === "ACCEPTED"
        ? "Retracted "
        : "";

    const displayTitle = getPageTitle(siteName);

    const getGroupName = () => {
      if (mainCategoryName) {
        return mainCategoryName;
      } else if (subjectType) {
        return subjectType.name;
      }

      return "";
    };
    const groupName = getGroupName();

    const pageTitle =
      warning + strippedTitle + " | " + groupName + " | " + displayTitle;

    const sortedAuthors = sorted(authors, "displayOrder");

    const originalUrl = original && original.url ? original.url : "";
    const largeThumbnailUrl =
      largeThumbnail && largeThumbnail.url ? largeThumbnail.url : "";

    let publishedDate;

    for (let i = 0; i < sortedAuthors.length; i++) {
      const author = sortedAuthors[i];

      authorGoogleMetas.push({
        name: "citation_author",
        content: `${author.firstName} ${author.lastName}`,
      });
      authorDCMetas.push({
        name: "DC:Contributor",
        content: `${author.firstName} ${author.lastName}`,
      });

      if (author.institutions && author.institutions.length > 0) {
        author.institutions.forEach((institution) => {
          authorGoogleMetas.push({
            name: "citation_author_institution",
            content: institution.name,
          });
        });
      }

      if (author.orcid) {
        authorGoogleMetas.push({
          name: "citation_author_orcid",
          content: `https://orcid.org/${author.orcid}`,
        });
      }
    }
    let shareaholicSiteId = this.$config.SHAREAHOLIC_SITE_ID;

    if (this.partner === "apsa") {
      shareaholicSiteId = this.$config.SHAREAHOLIC_APSA_SITE_ID;
    }

    if (article.publishedDate) {
      publishedDate = format(parseISO(article.publishedDate), "yyyy/MM/dd");
    } else if (article.statusDate) {
      publishedDate = format(parseISO(article.statusDate), "yyyy/MM/dd");
    }

    const canonicalUrl = toCanonicalUrl(`article-details/${id}`, partner);

    const meta = [
      { name: "description", hid: "description", content: strippedTitle },
      // Google Scholar SEO
      { name: "citation_title", content: strippedTitle },
      { name: "citation_publication_date", content: publishedDate },
      { name: "citation_doi", content: doi },
      { name: "citation_pdf_url", content: originalUrl },
      { name: "citation_keywords", content: keywords.join(";") },
      { name: "citation_abstract", content: strippedAbstract },
      ...authorGoogleMetas,
      // Facebook SEO
      { property: "og:title", content: strippedTitle },
      { property: "og:url", content: canonicalUrl },
      { property: "og:site_name", content: siteName },
      { property: "og:description", content: strippedAbstract },
      { property: "og:image", content: largeThumbnailUrl },
      // Dublin Core SEO
      { name: "DC:Language", content: "en" },
      { name: "DC:Title", content: strippedTitle },
      { name: "DC:Identifier", content: doi },
      // { name: 'DC:Contributor', content: authorName },
      ...authorDCMetas,
      { name: "DC:Date", content: publishedDate },
      {
        name: "shareaholic:site_id",
        content: shareaholicSiteId,
      },
    ];

    if (this.isBlocked) {
      meta.push({ name: "robots", content: "noindex" });
    }

    return {
      script: [
        { src: `${this.$config.SHAREAHOLIC_URL}`, defer: true },
        {
          src: "https://d1bxh8uas1mnw7.cloudfront.net/assets/embed.js",
          defer: true,
        },
        ...(this.currentTab === "connected-items"
          ? [{ src: this.networkMapUrl }]
          : []),
      ],
      title: pageTitle,
      meta,
      link: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  computed: {
    ...mapState({
      layout: (state) => state.theme.layout,
      siteName: (state) => getSiteName(state.theme),
      article: (state) => state.selectedItem,
      citationStyle: (state) => state.selectedOrigin.defaultCitationStyle,
      isBlocked: (state) => state.selectedItem.blocked,
      networkMapUrl: (state) => state.networkMapUrl,
    }),
    ...mapFlags([
      "articleDetailsShowNetworkMap",
      "articleDetailsShowComments",
      "newArticleDetailsDisplay",
    ]),
    partner() {
      return this.$config.partner;
    },
    displayIsMobile,
    displayIsTablet,
    displayIsDesktop,
    isLoggedIn() {
      return isLoggedIn(this.user);
    },
    solidHeader() {
      return this.layout.mainBannerSolidHeader;
    },
    mainCategory() {
      return this.article.mainCategory;
    },
    mainCategoryName() {
      return this.mainCategory?.name || "";
    },
    isResearchDirectionsItem() {
      return !!this.article.community && !!this.article.community.journalId;
    },
    user() {
      return this.$store.state.user;
    },
    currentTab: {
      set(tab) {
        this.$router.replace({ query: { ...this.$route.query, show: tab } });
      },
      get() {
        return this.$route.query.show;
      },
    },
    tabs() {
      return [
        {
          show: true,
          title: "Item",
          key: "item",
        },
        {
          show: true,
          title: "Connected Items",
          key: "connected-items",
        },
      ].filter((tab) => tab.show === true);
    },
  },
  watchQuery: ["playVideo"],
  mounted() {
    const { query } = this.$route;
    const { playVideo } = query || {};

    if (playVideo === "true") {
      this.autoplay = true;
    }
  },
  methods: {
    scrollToComments() {
      const focusElementId = this.isLoggedIn
        ? "loggedInUser"
        : "notLoggedInUser";
      const focusElement = document.getElementById(focusElementId);
      this.$vuetify.goTo(this.$refs.scrollElement, { offset: 64 });
      focusElement?.focus();
    },
    startVideo() {
      this.$store.dispatch("startVideoForSelectedItem", {
        params: { isStartingVideo: true },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.network-map {
  --network-map-font-family: #{inspect($body-font-family)};
  --network-map-alternative-font-family: #{inspect($body-font-family)};
}
</style>
